// Minimal dropdown menu primitive — enterprise hairline panel.
// Scope: trigger + anchored panel + items. No portal, no nested submenus,
// no virtualized lists — one anchor per menu is all current callers need.
// Close on outside pointer-down / Esc (same pattern as the create-org
// popover in Layout.tsx). Keyboard: Esc closes; items are plain focusable
// controls so Tab order works natively.
import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import { cn } from '@/lib/utils'

const Ctx = createContext<{ open: boolean; setOpen: (v: boolean) => void } | null>(null)

function useMenu() {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('DropdownMenu.* must render inside <DropdownMenu>')
  return ctx
}

export function DropdownMenu({ children, className }: { children: ReactNode; className?: string }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function onDown(e: MouseEvent) {
      if (!ref.current?.contains(e.target as Node)) setOpen(false)
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <Ctx.Provider value={{ open, setOpen }}>
      <div ref={ref} className={cn('relative inline-flex', className)}>
        {children}
      </div>
    </Ctx.Provider>
  )
}

export function DropdownMenuTrigger({
  children,
  className,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const { open, setOpen } = useMenu()
  return (
    <button
      type="button"
      aria-haspopup="menu"
      aria-expanded={open}
      onClick={() => setOpen(!open)}
      className={cn(
        'inline-flex h-[var(--control-h)] items-center gap-1.5 border border-enterprise-border bg-enterprise-surface px-3 text-sm font-medium tracking-tight text-enterprise-muted hover:border-enterprise-dim hover:text-enterprise-text motion-transition-fast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-enterprise-indigo focus-visible:ring-offset-2 focus-visible:ring-offset-enterprise-bg',
        className,
      )}
      {...props}
    >
      {children}
    </button>
  )
}

export function DropdownMenuContent({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) {
  const { open } = useMenu()
  if (!open) return null
  return (
    <div
      role="menu"
      className={cn(
        'absolute right-0 top-full z-50 mt-1 min-w-[180px] border border-enterprise-border bg-enterprise-surface p-1 shadow-hairline',
        className,
      )}
    >
      {children}
    </div>
  )
}

export function DropdownMenuItem({
  children,
  className,
  onClick,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const { setOpen } = useMenu()
  return (
    <button
      type="button"
      role="menuitem"
      onClick={(e) => {
        onClick?.(e)
        setOpen(false)
      }}
      className={cn(
        'flex h-9 w-full items-center gap-2 px-3 text-left text-sm tracking-tight text-enterprise-text hover:bg-enterprise-raised motion-transition-fast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-enterprise-indigo',
        className,
      )}
      {...props}
    >
      {children}
    </button>
  )
}

/** Non-interactive row — labels, embedded controls (theme/language pills). */
export function DropdownMenuRow({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('flex items-center gap-2 px-3 py-1.5', className)}>{children}</div>
}

export function DropdownMenuSeparator() {
  return <div role="separator" className="mx-1 my-1 h-px bg-enterprise-border" aria-hidden="true" />
}
